import { FocusEvent, useCallback, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { NakedButton } from '../../../../naked';
import { Button } from '../../../button';
import { Text } from '../../../text';
import { useTooltip } from '../../../tooltip';
import { DateNavigatorInput } from '../../atoms/date-navigator-input';
import { FieldLayout } from '../../layouts';
import { useDateNavigatorContext } from './date-navigator-context';
import { styles } from './date-navigator.styles';
import { DateNavigatorButtonWithInputProps } from './date-navigator.types';
import { DefaultButtonContent } from './default-button-content';

export const DateNavigatorButtonWithInput = ({
  trackingIds,
  selectorWidth = 134,
  inputPlaceholder,
  children,
  ...rest
}: DateNavigatorButtonWithInputProps) => {
  const { t } = useTranslation();
  const [showInputField, setShowInputField] = useState(false);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const {
    value,
    datePickerDialogProps,
    showError: showErrorFromContext,
    setOriginalValue,
    formattedDateObj,
    errorElement,
    onChange,
  } = useDateNavigatorContext();

  const { setOpen, Tooltip, triggerProps: tooltipTriggerProps, tooltipProps } = useTooltip({ hoverDelay: 1000 });
  const { triggerProps, open: popoverActive, toggle, debouncedBlur, debouncedFocus } = datePickerDialogProps;

  const placeholder = useMemo(() => {
    const todayDateObj = dayjs();
    return inputPlaceholder ? inputPlaceholder : `"${todayDateObj.format('MMM D')}" or ${todayDateObj.format('M/D')}`;
  }, [inputPlaceholder]);

  const handleBlur = useCallback(
    (event?: FocusEvent<HTMLInputElement>) => {
      debouncedBlur?.(event);
      setShowInputField(false);
    },
    [debouncedBlur]
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur(e);
    }
  };
  const isActive = popoverActive || showInputField;
  const showError = showErrorFromContext && !showInputField;

  return (
    <>
      <div
        css={[
          styles.dateSelectorContainer,
          styles.dateSelectorContainerState(isActive, showError),
          { width: selectorWidth },
        ]}
      >
        <Button
          {...triggerProps}
          css={styles.calendarIconWithInput}
          trackingId={trackingIds?.datePicker}
          variant={popoverActive ? 'tertiary' : 'secondary'}
          size='small'
          iconName='calendar'
          onClick={toggle}
        />
        {showInputField ? (
          <>
            <FieldLayout
              className='datepicker-input-field'
              fieldComponentProps={{ ref: inputFieldRef, autoFocus: true }}
              {...rest}
              field={DateNavigatorInput as ComponentProps<typeof FieldLayout>['field']}
              hideErrorText
              placeholder={placeholder}
              value={value}
              onBlur={handleBlur}
              onFocus={debouncedFocus}
              onChange={onChange}
              onKeyDown={handleKeyDown}
            />
          </>
        ) : (
          <>
            <NakedButton
              {...tooltipTriggerProps}
              css={styles.dateLabel}
              trackingId={trackingIds?.dateInput}
              onClick={() => {
                // Store the original value when showing the input field
                setOriginalValue(value);
                setShowInputField(true);
                onChange({ name: rest.name, value: '' });
                setOpen(false);
              }}
            >
              <DefaultButtonContent formattedDateObj={formattedDateObj} value={value} renderFn={children}>
                <Text weight='regular'>{formattedDateObj.day}</Text>
                <Text weight='bold'>{formattedDateObj.date}</Text>
              </DefaultButtonContent>
            </NakedButton>
            {formattedDateObj.isValid && (
              <Tooltip {...tooltipProps}>
                {t(`${formattedDateObj.day} ${formattedDateObj.date}, ${formattedDateObj.year}`)}
              </Tooltip>
            )}
            {showError && errorElement}
          </>
        )}
      </div>
    </>
  );
};
